import { css } from '@emotion/css';
import { useStore } from 'Shared/StoreProvider';
import { LabelOnLeftWrap } from 'Shared/forms/Wrap';
import { NumberField, selectStyle, SingleSelect } from 'Shared/forms/Inputs';
import { dbPatchFields } from 'Shared/forms/BaseOperations';
import { For, Match, Show, Switch } from 'solid-js';
import { Amount, defaultAmountUnits } from 'Shared/forms/Amount';
import { subdoc } from 'Common/ViewUtils';
import { theme } from 'Shared/ThemeProvider';


export function VerticalConstraint(P:{label:string,options:('parent'|'child'|'ratio'|'height')[],parent,child,ratio,height})
{
	const indentStyle = css({
		marginLeft: '3em'
	});

	const warningStyle = css(theme().warning);

	const sw = useStore();

	const changeType = async e => {
		if (e.target.value == 'parent') 
			await dbPatchFields(sw,{ratio:undefined,customRatio:undefined,height:undefined,parent:true,child:undefined});
		else  if (e.target.value == 'child') 
			await dbPatchFields(sw,{ratio:undefined,customRatio:undefined,height:undefined,parent:undefined,child:true});
		else if (e.target.value == 'ratio') 
			await dbPatchFields(sw,{ratio:'comp1x1',customRatio:undefined,height:undefined,parent:undefined,child:undefined});
		else if (e.target.value == 'height') 
			await dbPatchFields(sw,{ratio:undefined,customRatio:undefined,height:'80vh',parent:undefined,child:undefined});
	};

	return (
		<div>
			<LabelOnLeftWrap label={P.label}>
				<select class={selectStyle()} onChange={changeType} required> 
					<For each={P.options}>{option => 
						<Switch>
							<Match when={option == 'parent'}>
								<option value='parent' selected={P.parent}>Inherit</option>
							</Match>
							<Match when={option == 'child'}>
								<option value='child' selected={P.child}>Fit content</option>
							</Match>
							<Match when={option == 'ratio'}>
								<option value='ratio' selected={P.ratio!==undefined}>Fixed ratio</option>
							</Match>
							<Match when={option == 'height'}>
								<option value='height' selected={P.height!==undefined}>Explicit</option>
							</Match>
						</Switch>
					}</For>
				</select>
			</LabelOnLeftWrap>

			<Show when={P.ratio!==undefined}>
				<div class={indentStyle}>
					<RatioCrop {...P} ratioLabel='Aspect ratio' ratioRequired={true} includeCustomRatio />
				</div>
			</Show>

			<Show when={P.height!==undefined}>
				<LabelOnLeftWrap label='Amount' class={indentStyle}>
					<Amount field='height' options={defaultAmountUnits} />
				</LabelOnLeftWrap>
			</Show>

			<Show when={!heightOk(P,sw)}>
				<div class={warningStyle}>
					There is no parent height to inherit here.
				</div>
			</Show>
		</div>
	);
}

export function RatioCrop(P)
{
	const rowStyle = css({
		display: 'flex',
		justifyContent: 'flex-start',
		gap: '2em'
	});

//TODO add some more standard sizes? 2x1, 1x2?, 4x3, 3x4, 5x3?
	const options = {
		comp1x1: 'Square',
		comp3x2: 'Landscape (3x2)',
		comp2x3: 'Portrait (2x3)',
	};

	if (P.includeCustomRatio)
		options.custom = 'Custom';

	return (
		<div class={rowStyle}>
			<LabelOnLeftWrap narrow label={P.ratioLabel}>
				<SingleSelect field='ratio' options={options} required={P.ratioRequired} />
			</LabelOnLeftWrap>
			<Show when={P.ratio == 'custom'}>
				<LabelOnLeftWrap label='Ratio'>
					<NumberField field='customRatio' step='0.1' />
				</LabelOnLeftWrap>
			</Show>
		</div>
	);
}

//TODO add check for child/parent height conflicts.

function heightOk(P,sw)
{
	if (!P.parent)
		return true;

	const lower = subdoc(sw.store,sw.location.slice(0,-2));
	return !parentUsesChildHeight(sw.store,sw.location.slice(0, lower.layout ? -2 : -3));
}

/* Only look up one level: if the parent is also 'inherit' don't show the error - this is the parent's job. */
function parentUsesChildHeight(store,location)
{
	/* Note sections implicitly use child height */
	if (location.length <= 1)
		return true;

	let node = subdoc(store,location);
	if (!node.layout)
		node = subdoc(store,location.slice(0,-1));
	const layout = node.layout;

	/* Check the gaps that implicitly use child height: */
	if (layout=='vertical' || layout=='popup' || layout=='visualWithInfo') 
		return true;

	if (layout=='overlay' && location.at(-1)=='foreground')
		return true;

	/* Check that child height is not explicitly set: */
	if (layout=='grid') 
		return node.cellHeight.child;

	return node.height?.child;
}

